import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Button, ButtonGroup, CloseButton, Dialog, Field, Heading, IconButton, Input, Pagination, Portal, Show, Table, Text} from "@chakra-ui/react"
import {useToast} from "@chakra-ui/toast";
import {CompanyExecutorFull, dataApi, ExecutorTask as ExTask} from "../../api/data-api";
import {DateTime} from "luxon";
import {useRecoilState} from "recoil";
import {companyData, companyOperatorsData, executorData, userLoginResultData} from "../../components/hooks/recoilData";
import {checkAccess, errorNotify} from "../../components/hooks/CommonUtils";
import {LuChevronLeft, LuChevronRight} from "react-icons/lu";
import TaskItem from "./taskItem";
import {Tooltip} from "../../components/Tooltip";

interface ConfirmationInput {
    task: ExTask | null,
    calculationTimeHEstimated: number,
    calculationTimeHActual: number,
    calculationTimeHVerified: number,
}

export const Task = () => {
    const [items, setItems] = useState<ExTask[]>([]);
    const toast = useToast();
    const [pageSize] = useState<number>(10);
    const [count, setCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [userLoginResult] = useRecoilState(userLoginResultData);
    const [company] = useRecoilState(companyData);
    const [executor] = useRecoilState(executorData);
    const [companyOperators] = useRecoilState(companyOperatorsData);
    const [showItem, setShowItem] = useState<boolean>(false);
    const [currentItem, setCurrentItem] = useState<ExTask | null>(null);
    const [companyExecutors, setCompanyExecutors] = useState<CompanyExecutorFull[]>([]);
    const [showConfirmationDlg, setShowConfirmationDlg] = useState<boolean>(false);
    const inputDefaultConfirmation: ConfirmationInput = useMemo(() => {
        return {
            task: null,
            calculationTimeHEstimated: 0,
            calculationTimeHActual: 0,
            calculationTimeHVerified: 0,
        }
    }, []);
    const [inputParamsConfirmation, setInputParamsConfirmation] = useState<ConfirmationInput>(inputDefaultConfirmation);
    const prepareInputConfirmation = useCallback((item: ExTask | null) => {
        setInputParamsConfirmation({
            task: item ? item : null,
            calculationTimeHEstimated: item ? item.CALCULATION_TIME_H_ESTIMATED : 0,
            calculationTimeHActual: item ? (item.CALCULATION_TIME_H_ACTUAL ? item.CALCULATION_TIME_H_ACTUAL : item.CALCULATION_TIME_H_ESTIMATED) : 0,
            calculationTimeHVerified: item ? (item.CALCULATION_TIME_H_VERIFIED ? item.CALCULATION_TIME_H_VERIFIED : item.CALCULATION_TIME_H_ESTIMATED) : 0,
        });
    }, []);

    const loadItems = useCallback(() => {
        if (userLoginResult && (userLoginResult.isEmployer || userLoginResult.isOperator) && company) {
            dataApi.executorTaskListByCompany(company.ID, (currentPage - 1) * pageSize, pageSize)
                .then((o) => setItems(o))
                .catch((err) => errorNotify(err, toast));
        } else if (userLoginResult && userLoginResult.isExecutor && executor) {
            dataApi.executorTaskListByExecutor(executor.ID, (currentPage - 1) * pageSize, pageSize)
                .then((o) => setItems(o))
                .catch((err) => errorNotify(err, toast));
        }
    }, [toast, pageSize, company, currentPage, userLoginResult, executor]);
    useEffect(() => {
        loadItems();
        if (company) {
            dataApi.companyExecutorListFull(company.ID)
                .then((o) => {
                    setCompanyExecutors(o);
                })
                .catch((err) => errorNotify(err, toast));
        }
    }, [loadItems, toast, pageSize, company, currentPage]);
    useEffect(() => {
        if (userLoginResult && (userLoginResult.isEmployer || userLoginResult.isOperator) && company) {
            dataApi.executorTaskListCountByCompany(company.ID)
                .then((o) => {
                    if (o)
                        setCount(o as number);
                })
                .catch((err) => errorNotify(err, toast));
        } else if (userLoginResult && userLoginResult.isExecutor && executor) {
            dataApi.executorTaskListCountByExecutor(executor.ID)
                .then((o) => {
                    if (o)
                        setCount(o as number);
                })
                .catch((err) => errorNotify(err, toast));
        }
    }, [company, toast, userLoginResult, executor]);

    const setSend = useCallback((item: ExTask) => {
        if (!item)
            return;
        dataApi.executorTaskSend(item.ID)
            .then((o) => {
                if (o) {
                    toast({title: "Set send", status: "success"});
                    item.SENDED = new Date().getTime();
                    setItems(prev => {
                        const lst = [...prev];
                        let index = lst.indexOf(item);
                        if (index > -1)
                            lst[index] = item;
                        return lst;
                    });
                } else {
                    toast({title: "Set send failed", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    const setExecute = useCallback((item: ExTask, calculationTimeHActual: number) => {
        if (!item)
            return;
        dataApi.executorTaskExecute(item.ID, calculationTimeHActual)
            .then((o) => {
                if (o) {
                    toast({title: "Set execute", status: "success"});
                    item.EXECUTED = new Date().getTime();
                    setItems(prev => {
                        const lst = [...prev];
                        let index = lst.indexOf(item);
                        if (index > -1)
                            lst[index] = item;
                        return lst;
                    });
                } else {
                    toast({title: "Set execute failed", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    const setConfirm = useCallback((item: ExTask, calculationTimeHVerified: number) => {
        if (!item)
            return;
        dataApi.executorTaskConfirm(item.ID, calculationTimeHVerified)
            .then((o) => {
                if (o) {
                    toast({title: "Set confirm", status: "success"});
                    item.CONFIRMED = new Date().getTime();
                    setItems(prev => {
                        const lst = [...prev];
                        let index = lst.indexOf(item);
                        if (index > -1)
                            lst[index] = item;
                        return lst;
                    });
                } else {
                    toast({title: "Set confirm failed", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    const remove = useCallback((item: ExTask) => {
        if (!item)
            return;
        dataApi.executorTaskDelete(item.ID)
            .then((o) => {
                if (o) {
                    toast({title: "Delete", status: "success"});
                    setItems(prev => {
                        return prev.filter(o => o.ID !== item.ID);
                    });
                } else {
                    toast({title: "Delete", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    const goToItem = useCallback((item: ExTask | null) => {
        setShowItem(true);
        setCurrentItem(item);
    }, []);

    const goToItems = useCallback(() => {
        setShowItem(false);
        setCurrentItem(null);
    }, []);

    let checkAccessEdit = checkAccess(userLoginResult, company, companyOperators, "EDIT");

    const getFIO = useCallback((task: ExTask): string => {
        if (!task)
            return "";
        let e = companyExecutors.find(e => e.ID === task.COMPANY_EXECUTOR_ID);
        if (!e)
            return "" + task.COMPANY_EXECUTOR_ID;
        return `${e.FIRST_NAME} ${e.SECOND_NAME} ${e.LAST_NAME}`
    }, [companyExecutors]);

    const cancelConfirmationDlg = useCallback(() => {
        setShowConfirmationDlg(false);
        prepareInputConfirmation(null);
        toast({title: "Confirmation canceled", status: "warning"});
    }, [prepareInputConfirmation, setShowConfirmationDlg, toast]);

    const confirmConfirmationDlg = useCallback(() => {
        if (inputParamsConfirmation && inputParamsConfirmation.task) {
            if (userLoginResult && userLoginResult.isExecutor && inputParamsConfirmation.task.ACCEPTED && !inputParamsConfirmation.task.EXECUTED) {
                setExecute(inputParamsConfirmation.task, inputParamsConfirmation.calculationTimeHActual);
            } else if (userLoginResult && (userLoginResult.isOperator || userLoginResult.isEmployer) && inputParamsConfirmation.task && !inputParamsConfirmation.task.CONFIRMED) {
                setConfirm(inputParamsConfirmation.task, inputParamsConfirmation.calculationTimeHVerified);
            }
        }
        setShowConfirmationDlg(false);
        prepareInputConfirmation(null);
        toast({title: "Confirmation", status: "success"});
    }, [prepareInputConfirmation, setShowConfirmationDlg, toast, setConfirm, setExecute, inputParamsConfirmation, userLoginResult]);

    const createPay = useCallback((item: ExTask) => {
        if (!item || !company || !item.RESULT_VALUE)
            return;
        const e = companyExecutors.find(e => e.ID === item.COMPANY_EXECUTOR_ID);
        if (!e)
            return;
        dataApi.payoutListCreateOrUpdate(null, "Оплата задания " + item.ID, "STANDARD", company.ID)
            .then((value) => {
                toast({title: `Реестр ${value.ID} создан`, status: "success"});
                return dataApi.payoutCreateOrUpdate(null, item.RESULT_VALUE, `${e.FIRST_NAME} ${e.SECOND_NAME} ${e.LAST_NAME}`, e.CODE_INN, "", e.WITHDRAW_TYPE,
                    e.WITHDRAW_SPB_TEL, e.WITHDRAW_SPB_TEL_BANK_ID, e.WITHDRAW_CARD_NUMBER, e.WITHDRAW_ACCOUNT_NUMBER,
                    e.WITHDRAW_BIC, value.ID, e.USER_ID_OWN, item.EXECUTOR_CONTRACT_ID, item.ID)
            })
            .then((value) => {
                toast({title: `Выплата ${value.ID} в реестре ${value.PAYOUT_LIST_ID} создана`, status: "success"});
                item.PAID = new Date().getTime();
                setItems(prev => {
                    const lst = [...prev];
                    let index = lst.indexOf(item);
                    if (index > -1)
                        lst[index] = item;
                    return lst;
                });
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast, company, companyExecutors]);

    return showItem ?
        <>
            <Button onClick={e => goToItems()}>
                Назад
            </Button>
            <TaskItem task={currentItem} setTaskAndReturn={t => {
                setItems(prev => {
                    const lst = [...prev];
                    let findO = lst.find(o => o.ID === t.ID);
                    if (findO) {
                        lst[lst.indexOf(findO)] = t;
                    } else {
                        lst.push(t);
                    }
                    return lst;
                });
                goToItems()
            }}/>
        </>
        :
        <Box borderWidth={1} borderColor="gray">
            <Heading size="xl">Задачи</Heading>
            <Show when={userLoginResult && (userLoginResult.isEmployer || userLoginResult.isOperator)}>
                <Button onClick={e => goToItem(null)}>
                    Создать новую
                </Button>
            </Show>

            <Dialog.Root open={showConfirmationDlg} onOpenChange={(v: any) => {
                if (v.open) {
                    setShowConfirmationDlg(v.open);
                } else {
                    cancelConfirmationDlg();
                }
            }}>
                <Dialog.Trigger/>
                <Portal>
                    <Dialog.Backdrop/>
                    <Dialog.Positioner>
                        <Dialog.Content>
                            <Dialog.Header>
                                <Dialog.Title>Подтверждение</Dialog.Title>
                            </Dialog.Header>
                            <Dialog.Body>
                                <Show when={inputParamsConfirmation.task && inputParamsConfirmation.task.CALCULATION_TYPE === "HOUR"}>
                                    <Field.Root orientation="horizontal">
                                        <Tooltip showArrow content="format HH:mm dd.MM.yyyy">
                                            <Field.Label>Предполагаемое количество часов </Field.Label>
                                        </Tooltip>
                                        <Input type="text" placeholder="Количество"
                                               disabled={true}
                                               value={inputParamsConfirmation.calculationTimeHEstimated}
                                               onChange={(it) => setInputParamsConfirmation((prevState) => {
                                                   return {...prevState, calculationTimeHEstimated: parseInt(it.target.value)}
                                               })}/>
                                    </Field.Root>
                                    <Field.Root orientation="horizontal">
                                        <Tooltip showArrow content="format HH:mm dd.MM.yyyy">
                                            <Field.Label>Указанное количество часов </Field.Label>
                                        </Tooltip>
                                        <Input type="text" placeholder="Количество"
                                               disabled={/*!userLoginResult || !userLoginResult.isExecutor*/true}
                                               value={inputParamsConfirmation.calculationTimeHActual}
                                               onChange={(it) => setInputParamsConfirmation((prevState) => {
                                                   return {...prevState, calculationTimeHActual: parseInt(it.target.value)}
                                               })}/>
                                    </Field.Root>
                                    <Field.Root orientation="horizontal">
                                        <Tooltip showArrow content="format HH:mm dd.MM.yyyy">
                                            <Field.Label>Принятое количество часов </Field.Label>
                                        </Tooltip>
                                        <Input type="text" placeholder="Количество"
                                               disabled={!userLoginResult || !userLoginResult.isOperator}
                                               value={inputParamsConfirmation.calculationTimeHVerified}
                                               onChange={(it) => setInputParamsConfirmation((prevState) => {
                                                   return {...prevState, calculationTimeHVerified: parseInt(it.target.value)}
                                               })}/>
                                    </Field.Root>
                                </Show>
                                <Show when={inputParamsConfirmation.task && inputParamsConfirmation.task.CALCULATION_TYPE === "TASK"}>
                                    <Text>Подтвердить?</Text>
                                </Show>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Dialog.ActionTrigger asChild>
                                    <Button variant="outline" onClick={cancelConfirmationDlg}>Отмена</Button>
                                </Dialog.ActionTrigger>
                                <Button onClick={confirmConfirmationDlg}>Подтвердить</Button>
                            </Dialog.Footer>
                            <Dialog.CloseTrigger asChild>
                                <CloseButton size="sm" onClick={cancelConfirmationDlg}/>
                            </Dialog.CloseTrigger>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>

            <Table.Root size="sm" variant="outline" striped>
                <Table.Header>
                    <Table.Row>
                        <Table.ColumnHeader>№</Table.ColumnHeader>
                        <Table.ColumnHeader>Исполнитель</Table.ColumnHeader>
                        <Table.ColumnHeader>Название</Table.ColumnHeader>
                        <Table.ColumnHeader>Проект</Table.ColumnHeader>
                        <Table.ColumnHeader>Период</Table.ColumnHeader>
                        <Table.ColumnHeader>Адресс</Table.ColumnHeader>
                        <Table.ColumnHeader>Стоимость</Table.ColumnHeader>
                        <Table.ColumnHeader>Изменен</Table.ColumnHeader>
                        <Table.ColumnHeader>Отправлен</Table.ColumnHeader>
                        <Table.ColumnHeader>Принят</Table.ColumnHeader>
                        <Table.ColumnHeader>Исполнен</Table.ColumnHeader>
                        <Table.ColumnHeader>Подтвержден</Table.ColumnHeader>
                        <Table.ColumnHeader>Оплачен</Table.ColumnHeader>
                        <Table.ColumnHeader>Действия</Table.ColumnHeader>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {items.map((item) => (
                        <Table.Row key={item.ID}>
                            <Table.Cell>{item.ID}</Table.Cell>
                            <Table.Cell>{getFIO(item)}</Table.Cell>
                            <Table.Cell>{item.NAME}</Table.Cell>
                            <Table.Cell>{item.PROJECT}</Table.Cell>
                            <Table.Cell>
                                {item.START_DATE ? DateTime.fromMillis(item.START_DATE).toFormat('dd.MM.yyyy') : ""}
                                <br/>
                                {item.END_DATE ? DateTime.fromMillis(item.END_DATE).toFormat('dd.MM.yyyy') : ""}
                            </Table.Cell>
                            <Table.Cell>{item.ADDRESS}</Table.Cell>
                            <Table.Cell>{item.RESULT_VALUE ? item.RESULT_VALUE : item.VALUE1}</Table.Cell>
                            <Table.Cell>{item.VERSION ? DateTime.fromMillis(item.VERSION).toFormat('HH:mm dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.SENDED ? DateTime.fromMillis(item.SENDED).toFormat('HH:mm dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.ACCEPTED ? DateTime.fromMillis(item.ACCEPTED).toFormat('HH:mm dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.EXECUTED ? DateTime.fromMillis(item.EXECUTED).toFormat('HH:mm dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.CONFIRMED ? DateTime.fromMillis(item.CONFIRMED).toFormat('HH:mm dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>{item.PAID ? DateTime.fromMillis(item.PAID).toFormat('HH:mm dd.MM.yyyy') : ""}</Table.Cell>
                            <Table.Cell>
                                <Button onClick={e => goToItem(item)}>{!item.SENDED && !item.CONFIRMED ? "Изменить" : "Показать"}</Button>
                                <Show when={userLoginResult && (userLoginResult.isOperator || userLoginResult.isEmployer)}>
                                    <Button
                                        display={item.SENDED ? "none" : "block"}
                                        disabled={!checkAccessEdit}
                                        onClick={e => setSend(item)}>Отправить</Button>
                                    <Button
                                        display={!item.EXECUTED || item.CONFIRMED ? "none" : "block"}
                                        disabled={!checkAccessEdit}
                                        onClick={e => {
                                            prepareInputConfirmation(item);
                                            setShowConfirmationDlg(true);
                                        }}>Подтвердить</Button>
                                    <Button
                                        display={item.SENDED ? "none" : "block"}
                                        disabled={!checkAccessEdit}
                                        onClick={e => remove(item)}>Удалить</Button>
                                    <Button
                                        display={item.PAID ? "none" : "block"}
                                        disabled={!checkAccessEdit}
                                        onClick={e => createPay(item)}>Оплатить</Button>
                                </Show>
                                <Show when={userLoginResult && userLoginResult.isExecutor}>
                                    <Button
                                        display={!item.ACCEPTED || item.EXECUTED ? "none" : "block"}
                                        onClick={e => {
                                            prepareInputConfirmation(item);
                                            setShowConfirmationDlg(true);
                                        }}>Исполнить</Button>
                                </Show>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table.Root>

            <Pagination.Root count={count} pageSize={pageSize} page={currentPage}>
                <ButtonGroup variant="ghost" size="sm" wrap="wrap">
                    <Pagination.PrevTrigger asChild>
                        <IconButton>
                            <LuChevronLeft/>
                        </IconButton>
                    </Pagination.PrevTrigger>

                    <Pagination.Items
                        render={(page) => (
                            <IconButton variant={{base: "ghost", _selected: "outline"}} onClick={e => setCurrentPage(page.value)}>
                                {page.value}
                            </IconButton>
                        )}
                    />

                    <Pagination.NextTrigger asChild>
                        <IconButton>
                            <LuChevronRight/>
                        </IconButton>
                    </Pagination.NextTrigger>
                </ButtonGroup>
            </Pagination.Root>
        </Box>
};